/**
 * LikeButton — optimistic, auth-gated like control. Uses the PURE
 * {@link likeReducer} for the state machine (optimistic toggle → settle/revert)
 * and calls the `like`/`unlike` callables. A signed-out (or anonymous) user is
 * blocked with a prompt rather than a write attempt.
 */
import { useReducer, useEffect } from 'react';
import { useAuth, useServices } from '../services/context.js';
import {
  likeReducer,
  initLikeState,
  type LikeState,
  type LikeAction,
} from '../game/likeReducer.js';

export interface LikeButtonProps {
  creationId: string;
  initialLiked: boolean;
  initialCount: number;
  /** Live server-known liked flag (from onSnapshot), reconciled when idle. */
  serverLiked?: boolean;
  /** Live server-known count (from the creation doc), reconciled when idle. */
  serverCount?: number;
}

export function LikeButton({
  creationId,
  initialLiked,
  initialCount,
  serverLiked,
  serverCount,
}: LikeButtonProps) {
  const { user } = useAuth();
  const services = useServices();
  const [state, dispatch] = useReducer(
    likeReducer as (s: LikeState, a: LikeAction) => LikeState,
    initLikeState(initialLiked, initialCount),
  );

  // Reconcile with live server values when no write is pending.
  useEffect(() => {
    if (serverLiked === undefined && serverCount === undefined) return;
    dispatch({
      type: 'sync',
      liked: serverLiked ?? state.liked,
      count: serverCount ?? state.count,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverLiked, serverCount]);

  const canLike = Boolean(user && !user.isAnonymous);

  async function onClick() {
    if (!canLike) {
      dispatch({ type: 'blocked', reason: 'Sign in to like games.' });
      return;
    }
    const wasLiked = state.liked;
    dispatch({ type: 'toggle' });
    try {
      if (wasLiked) await services.callable.unlike(creationId);
      else await services.callable.like(creationId);
      dispatch({ type: 'settled' });
    } catch {
      dispatch({ type: 'failed' });
    }
  }

  return (
    <div className="stack">
      <button
        className={`like-btn ${state.liked ? 'like-btn--liked' : ''}`}
        onClick={() => void onClick()}
        disabled={state.pending}
        aria-pressed={state.liked}
        data-testid="like-button"
      >
        <span>{state.liked ? '♥' : '♡'}</span>
        <span data-testid="like-count">{state.count}</span>
      </button>
      {state.blockedReason && <span className="muted">{state.blockedReason}</span>}
    </div>
  );
}
